import type {
    WorkActivity,
    WorkActivityStatus,
    WorkActivityType,
} from '@/lib/api/work-activity-api'
import { m } from '@/lib/paraglide/messages'

/** Nhãn tiếng Việt của từng loại hoạt động. */
export const workActivityTypeLabel: Record<WorkActivityType, () => string> = {
    task: m.work_activity_type_task,
    business_trip: m.work_activity_type_business_trip,
    meeting: m.work_activity_type_meeting,
    event: m.work_activity_type_event,
}

/** Nhãn tiếng Việt của từng cột bảng Kanban. */
export const workActivityStatusLabel: Record<WorkActivityStatus, () => string> =
    {
        open: m.work_activity_status_open,
        in_progress: m.work_activity_status_in_progress,
        done: m.work_activity_status_done,
        closed: m.work_activity_status_closed,
    }

/**
 * Giờ Việt Nam lệch UTC cố định +7 và KHÔNG có quy ước giờ mùa hè, nên một hằng
 * số bù phút là đủ — không cần thư viện múi giờ.
 */
const VN_OFFSET_MINUTES = 7 * 60
const MS_PER_MINUTE = 60_000

/** Ghép các thành phần đã tính sẵn thành 'YYYY-MM-DDTHH:mm'. */
function pad(n: number): string {
    return String(n).padStart(2, '0')
}

/**
 * Đổi mốc ISO của API sang giá trị `<input type="datetime-local">`
 * ('YYYY-MM-DDTHH:mm') **theo giờ Việt Nam**.
 *
 * Ô nhập datetime-local không mang múi giờ: nó hiện đúng chuỗi mình đưa vào. Nếu
 * dựng chuỗi đó từ `getHours()` thì nó là giờ của MÁY đang mở app — một cuộc họp
 * 08:00 giờ VN sẽ hiện 01:00 trên máy đặt múi giờ châu Âu. Ở đây cộng thẳng +7
 * rồi đọc bằng `getUTC*`, nên kết quả không phụ thuộc đồng hồ máy.
 */
export function toVnInput(iso: string | null | undefined): string {
    if (!iso) return ''
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    const vn = new Date(d.getTime() + VN_OFFSET_MINUTES * MS_PER_MINUTE)
    return (
        `${vn.getUTCFullYear()}-${pad(vn.getUTCMonth() + 1)}-${pad(vn.getUTCDate())}` +
        `T${pad(vn.getUTCHours())}:${pad(vn.getUTCMinutes())}`
    )
}

/**
 * Đọc giá trị `<input type="datetime-local">` như **giờ Việt Nam** rồi trả ISO
 * để gửi lên API.
 *
 * `new Date('2026-09-06T14:30')` hiểu chuỗi đó theo múi giờ của máy; thêm hậu tố
 * `+07:00` là ghim nó về giờ VN.
 */
export function fromVnInput(value: string): string | null {
    if (!value) return null
    const ms = Date.parse(`${value}:00+07:00`)
    if (Number.isNaN(ms)) return null
    return new Date(ms).toISOString()
}

/**
 * Mốc để đưa vào lịch: một `Date` mà **giờ địa phương của trình duyệt** trùng
 * đúng giờ Việt Nam của mốc gốc.
 *
 * Thư viện lịch vẽ ô và nhãn giờ theo giờ địa phương, không nhận tham số múi
 * giờ. Dịch trước như thế này là cách duy nhất để lưới lịch luôn là giờ VN.
 * Đây CHỈ dùng để hiển thị — không bao giờ gửi ngược lên API.
 */
export function toCalendarDate(iso: string): Date {
    const vn = new Date(
        new Date(iso).getTime() + VN_OFFSET_MINUTES * MS_PER_MINUTE,
    )
    return new Date(
        vn.getUTCFullYear(),
        vn.getUTCMonth(),
        vn.getUTCDate(),
        vn.getUTCHours(),
        vn.getUTCMinutes(),
        0,
        0,
    )
}

/**
 * Đổi một mốc hiển thị của lịch (giờ địa phương đã là giờ VN) sang giá trị ô
 * nhập. Dùng khi người dùng bấm vào một ô trống để tạo hoạt động tại giờ đó.
 */
export function calendarDateToVnInput(d: Date): string {
    return (
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
        `T${pad(d.getHours())}:${pad(d.getMinutes())}`
    )
}

/**
 * Nhiệm vụ đã quá hạn mà chưa hoàn thành / chưa đóng.
 *
 * Hạn chót của nhiệm vụ là `ends_at` — đầu cuối của khung giờ, giống lịch công
 * tác. Hai loại lịch không có khái niệm quá hạn nên luôn trả false.
 */
export function isOverdue(
    activity: WorkActivity,
    now: Date = new Date(),
): boolean {
    if (activity.type !== 'task' || !activity.ends_at) return false
    if (activity.status === 'done' || activity.status === 'closed') return false
    return new Date(activity.ends_at).getTime() < now.getTime()
}

/** Vai trò của người đang thao tác đối với đúng một hoạt động. */
export interface WorkActivityRoles {
    isCreator: boolean
    isAssignee: boolean
    canClose: boolean
}

/** Vai trò của `userId` trong `activity`, kèm quyền nghiệm thu toàn hệ thống. */
export function rolesFor(
    activity: WorkActivity,
    userId: string | undefined,
    canClose: boolean,
): WorkActivityRoles {
    return {
        isCreator: !!userId && activity.created_by === userId,
        // Giao toàn thể nhân viên thì ai cũng là người được giao — bản sao
        // của `IsAssignee` phía server (#M-154).
        isAssignee:
            !!userId &&
            (activity.assign_all ||
                activity.assignees.some((a) => a.user_id === userId)),
        canClose,
    }
}

/**
 * Bản sao ở phía client của luật chuyển trạng thái trong usecase Go.
 *
 * Mục đích là báo lỗi ngay khi người dùng thả thẻ, thay vì đợi một vòng mạng.
 * KHÔNG phải cổng bảo mật — backend vẫn xét lại đúng luật này; đây chỉ là để
 * người dùng biết vì sao thẻ bật về chỗ cũ.
 *
 * Trả về `null` khi hợp lệ, hoặc câu tiếng Việt giải thích lý do bị chặn.
 */
export function dragCheck(
    from: WorkActivityStatus,
    to: WorkActivityStatus,
    roles: WorkActivityRoles,
): string | null {
    if (from === to) return null

    const canWork = roles.isAssignee || roles.isCreator
    const canClose = roles.isCreator || roles.canClose

    if (from === 'closed') {
        if (to !== 'open') return m.work_activity_drag_reopen_to_open_only()
        return canClose ? null : m.work_activity_drag_close_not_allowed()
    }
    if (to === 'closed') {
        if (from !== 'done') return m.work_activity_drag_must_complete_first()
        return canClose ? null : m.work_activity_drag_close_not_allowed()
    }
    if (from === 'done' && to === 'open') {
        return canClose ? null : m.work_activity_drag_close_not_allowed()
    }
    return canWork ? null : m.work_activity_drag_not_yours()
}
